import {
  Box,
  Heading,
  HStack,
  Icon,
  Integer,
  Show,
  Text,
  ValueByDevice,
  VStack,
} from 'platform/foundation';
import {css, useTheme} from 'styled-components';
import {match} from 'ts-pattern';

import {isNil, isNotEmpty} from 'ramda';

import {suffixTestId, TestIdProps} from 'shared/web';

import {
  InspectionReportBlock,
  InspectionReportConclusion,
  InspectionReportListItem,
  InspectionReportModel,
  InspectionReportVerdict,
} from './utils/parseInspectionReport';

interface InspectionReportProps extends TestIdProps {
  report: InspectionReportModel;
}

export function InspectionReport(props: InspectionReportProps) {
  return (
    <VStack spacing={6} data-testid={suffixTestId('inspectionReport', props)}>
      <InspectionReportVerdictView
        verdict={props.report.verdict}
        data-testid={suffixTestId('verdict', props)}
      />

      {props.report.sections.map((section, sectionIndex) => (
        <Box
          // eslint-disable-next-line react/no-array-index-key -- sections have no identity; the report is static
          key={sectionIndex}
          paddingHorizontal={SECTION_INDENT}
          data-testid={suffixTestId(`section-[${sectionIndex}]`, props)}
        >
          <VStack spacing={3}>
            <Show when={isNotEmpty(section.title)}>
              <Heading size={3} headingLevel={REPORT_HEADING_LEVEL}>
                {section.title}
              </Heading>
            </Show>
            <VStack spacing={4}>
              {section.blocks.map((block, blockIndex) => (
                // eslint-disable-next-line react/no-array-index-key -- blocks have no identity; the report is static
                <InspectionReportBlockView key={blockIndex} block={block} />
              ))}
            </VStack>
          </VStack>
        </Box>
      ))}

      <InspectionReportConclusionView
        conclusion={props.report.conclusion}
        data-testid={suffixTestId('conclusion', props)}
      />
    </VStack>
  );
}

function InspectionReportVerdictView(
  props: {verdict: InspectionReportVerdict | null} & TestIdProps
) {
  if (isNil(props.verdict)) {
    return null;
  }

  return (
    <Box
      backgroundColor="severity.successMedium"
      borderRadius="large"
      padding={[6, 8, 8, 8]}
      data-testid={props['data-testid']}
    >
      <VStack spacing={4}>
        <HStack spacing={4} align="center">
          <Heading size={3} headingLevel={REPORT_HEADING_LEVEL}>
            {props.verdict.headline}
          </Heading>
          <Icon value="action/thumb_up" size={7} color="severity.successBase" />
        </HStack>
        <Text size="base" color="secondary">
          {props.verdict.reason}
        </Text>
      </VStack>
    </Box>
  );
}

function InspectionReportConclusionView(
  props: {conclusion: InspectionReportConclusion | null} & TestIdProps
) {
  if (isNil(props.conclusion)) {
    return null;
  }

  return (
    <Box paddingHorizontal={SECTION_INDENT} data-testid={props['data-testid']}>
      <VStack spacing={3}>
        <Show when={isNotEmpty(props.conclusion.eyebrow)}>
          <Text size="small" color="tertiary">
            {props.conclusion.eyebrow}
          </Text>
        </Show>
        <Heading size={3} headingLevel={REPORT_HEADING_LEVEL}>
          {props.conclusion.headline}
        </Heading>
        <Text size="base" color="secondary">
          {props.conclusion.text}
        </Text>
      </VStack>
    </Box>
  );
}

function InspectionReportBlockView(props: {block: InspectionReportBlock}) {
  return match(props.block)
    .with({type: 'paragraph'}, (paragraph) => (
      <Text size="base" color="secondary" overflowWrap="anywhere">
        {paragraph.text}
      </Text>
    ))
    .with({type: 'list'}, (list) => <InspectionReportListView items={list.items} />)
    .exhaustive();
}

function InspectionReportListView(props: {items: InspectionReportListItem[]}) {
  const theme = useTheme();

  return (
    <ul
      // `all: unset` clears the browser's default list spacing; the parent VStack owns the spacing.
      css={css`
        all: unset;
        display: flex;
        flex-direction: column;
        gap: ${theme.getSize(2)};
        padding-left: ${theme.getSize(5)};
        list-style: disc;
        color: ${theme.colors.text.secondary};
      `}
    >
      {props.items.map((item, itemIndex) => (
        // eslint-disable-next-line react/no-array-index-key -- list items have no identity; the report is static
        <li key={itemIndex}>
          <Text size="base" color="secondary" overflowWrap="anywhere">
            <Show when={isNotEmpty(item.lead)}>
              <strong>{item.lead}</strong>{' '}
            </Show>
            {item.text}
          </Text>
        </li>
      ))}
    </ul>
  );
}

// Sections sit inset from the verdict box, which runs full width.
const SECTION_INDENT: ValueByDevice<Integer> = [0, 6, 6, 6];
// The report sits under the level-1 section heading of the certificate card.
const REPORT_HEADING_LEVEL = 2;
